import { t as uiText } from '../i18n';
import React from 'react';
import { useParams } from 'react-router-dom';
import FileList from './FileList';
import FileStatsDisplay from './FileStatsDisplay';
import FileDeleteModal from './FileDeleteModal';
import { deleteMultipleFiles } from '../features/files/fileUtils';
import { useAuthContext } from '../context/AuthContext';
import { deleteThreadTempFileByAttachmentId } from '../features/threads/threadTempFiles';

interface FileListViewProps {
  refreshTrigger?: number;
  onRefresh?: () => void;
}

const FileListView: React.FC<FileListViewProps> = ({ 
  refreshTrigger = 0,
  onRefresh 
}) => {
  const { workspaceId: routeWorkspaceId } = useParams();
  const { user } = useAuthContext();
  
  // レスポンシブ対応用の状態
  const [isMobile, setIsMobile] = React.useState(false);
  
  // 選択モード関連の状態
  const [isSelectionMode, setIsSelectionMode] = React.useState(false);
  const [selectedFileIds, setSelectedFileIds] = React.useState<string[]>([]);
  
  // 削除モーダル関連の状態
  const [showDeleteModal, setShowDeleteModal] = React.useState(false);
  const [isDeletingFiles, setIsDeletingFiles] = React.useState(false);
  
  // リフレッシュ用の状態
  const [internalRefreshTrigger, setInternalRefreshTrigger] = React.useState(0);

  // 選択モードの切り替え
  const handleSelectionModeToggle = () => {
    setIsSelectionMode(!isSelectionMode);
    setSelectedFileIds([]); // 選択をクリア
  };

  // 選択状態の変更
  const handleSelectionChange = (newSelectedIds: string[]) => {
    setSelectedFileIds(newSelectedIds);
  };

  // 削除ボタンクリック時の処理
  const handleDeleteClick = () => {
    if (selectedFileIds.length > 0) {
      setShowDeleteModal(true);
    }
  };

  // 削除モーダルのキャンセル
  const handleDeleteCancel = () => {
    setShowDeleteModal(false);
  };

  // 削除モーダルの確認
  const handleDeleteConfirm = async () => {
    if (!routeWorkspaceId || !user) {
      console.error('ワークスペースまたはユーザー情報がありません');
      return;
    }

    setIsDeletingFiles(true);
    
    try {
      const permanentIds = selectedFileIds
        .filter((k) => k.startsWith('permanent:'))
        .map((k) => k.slice('permanent:'.length));
      const tempIds = selectedFileIds
        .filter((k) => k.startsWith('temporary:'))
        .map((k) => k.slice('temporary:'.length));

      const permResult = permanentIds.length > 0
        ? await deleteMultipleFiles({ workspaceId: routeWorkspaceId, fileIds: permanentIds, userId: user.uid })
        : { succeeded: [], failed: [], totalSizeDeleted: 0, totalCountDeleted: 0 };

      // 一時ファイルは threadTempFiles を起点に削除（スレッド側の表示置換は後続タスクで対応）
      const tempSucceeded: string[] = [];
      const tempFailed: Array<{ fileId: string; error: string }> = [];
      for (const id of tempIds) {
        try {
          const r = await deleteThreadTempFileByAttachmentId({ workspaceId: routeWorkspaceId, attachmentId: id, userId: user.uid });
          if (r) tempSucceeded.push(id);
        } catch (e: any) {
          tempFailed.push({ fileId: id, error: String(e?.message || e) });
        }
      }

      const result = {
        succeeded: [...permResult.succeeded, ...tempSucceeded],
        failed: [...permResult.failed, ...tempFailed],
      };

      setInternalRefreshTrigger(prev => {
        const newTrigger = prev + 1;
        return newTrigger;
      });

      // 外部のリフレッシュコールバックも呼び出し
      if (onRefresh) {
        onRefresh();
      }

      // リフレッシュ完了を待ってから結果表示
      setTimeout(() => {
        // 削除結果のフィードバック
        if (result.succeeded.length > 0 && result.failed.length === 0) {
          // 全て成功
          alert(uiText("Deleted {{length}} files.", { length: result.succeeded.length }));
        } else if (result.succeeded.length > 0 && result.failed.length > 0) {
          // 部分的に成功
          alert(uiText("Deleted {{length}} files.\nFailed to delete {{length_2}} files.", { length: result.succeeded.length, length_2: result.failed.length }));
        } else if (result.failed.length > 0) {
          // 全て失敗
          alert(uiText("Failed to delete file. Please try again in a little while."));
        }
      }, 300); // 300ms待機してリフレッシュ完了を確保
    } catch (error) {
      console.error('🗑️ ファイル削除エラー', error);
      alert(uiText("An error occurred while deleting the file. Please try again in a little while."));
    } finally {
      setIsDeletingFiles(false);
      
      // モーダルを閉じる処理を少し遅延させてリフレッシュを確実に実行
      setTimeout(() => {
        setShowDeleteModal(false);
        setSelectedFileIds([]);
        setIsSelectionMode(false);
      }, 500); // 500ms後にモーダル関連の状態をクリア
    }
  };

  // レスポンシブ対応のウィンドウサイズ監視
  React.useEffect(() => {
    const checkIsMobile = () => {
      setIsMobile(window.innerWidth <= 768);
    };
    
    checkIsMobile();
    window.addEventListener('resize', checkIsMobile);
    
    return () => {
      window.removeEventListener('resize', checkIsMobile);
    };
  }, []);

  return (
    <div style={{ marginTop: '20px' }}>
      {/* ファイル統計表示 */}
      <div style={{ marginBottom: '24px' }}>
        <FileStatsDisplay 
          showVerifyButton={false} 
          refreshTrigger={refreshTrigger + internalRefreshTrigger}
        />
      </div>

      {/* ファイル一覧コンポーネント */}
      <FileList
        refreshTrigger={refreshTrigger + internalRefreshTrigger}
        isMobile={isMobile}
        isSelectionMode={isSelectionMode}
        selectedFileIds={selectedFileIds}
        onSelectionChange={handleSelectionChange}
        onSelectionModeToggle={handleSelectionModeToggle}
        onDeleteClick={handleDeleteClick}
      />

      {/* 削除確認モーダル */}
      <FileDeleteModal
        isOpen={showDeleteModal}
        onClose={handleDeleteCancel}
        onConfirm={handleDeleteConfirm}
        selectedFileCount={selectedFileIds.length}
        hasReferencedFiles={selectedFileIds.length > 0} // 現時点では選択されたファイルがあれば参照ファイルありと仮定
        isDeleting={isDeletingFiles}
      />
    </div>
  );
};

export default FileListView;
